import { useCallback, useMemo } from 'react';
import { Table } from '@carboledger/ui';
import { buildTableLabels } from '@/config/tableLabels';
import {
  useTableState,
  useTranslations,
  useGenuinelyEmpty,
  UnitTypeContext,
  useUnitTypeHeader,
} from '@/hooks';
import { EMPTY_LIST } from '@/lib/utils';
import { useGetAllocationsQuery, useGetAllocationFilterOptionsQuery } from '../api/allocationsApi';
import { buildAllocationsColumns } from '../constants/allocationsColumns';
import { buildAllocationFilterDefinitions } from '../constants/allocationFilters';
import { buildUnitHeaderOptions } from '../constants/allocationUnits';

const getAllocationRowId = (row) => row.id;

/**
 * Props:
 *   className        forwarded to <Table>
 *   extraFilters             filters merged on top of the panel's, e.g. the inventory drill-down's
 *   certificateType   the module's `CertificateType`, which narrows the list
 *   emptyState        <Table>'s emptyState — the caller owns its copy and CTA
 *   noResults         <Table>'s noResults
 *   onOpen            (row) => void — makes the id cell a link; omit for plain text
   onViewDeclaration  (row) => void — opens the row’s generated declaration
 *   actionsWidth      <Table>’s actionsWidth
 *   onEmptyChange     (isEmpty) => void — true only when nothing matches and nothing is filtered
 *   rowActions        <Table>'s rowActions
 *   rowMenuActions    <Table>'s rowMenuActions
 *   bulkActions       <Table>'s bulkActions — selectable follows from it
 *   tableRef          forwarded to <Table>, for clearSelection
 */
export const AllocationsTable = ({
  className,
  extraFilters,
  certificateType,
  emptyState,
  noResults,
  onOpen,
  onViewDeclaration,
  actionsWidth,
  onEmptyChange,
  rowActions,
  rowMenuActions,
  bulkActions,
  tableRef,
}) => {
  const t = useTranslations();
  const table = useTableState();

  const queryArgs = useMemo(
    () => ({ certificateType, ...table.queryParams, extraFilters }),
    [certificateType, table.queryParams, extraFilters],
  );
  const { data, isFetching, isError } = useGetAllocationsQuery(queryArgs);
  const rows = data?.rows ?? EMPTY_LIST;
  const total = data?.totalCount ?? 0;

  useGenuinelyEmpty({ table, isFetching, isError, total }, onEmptyChange);

  const { unitHeaderSelect, contextValue } = useUnitTypeHeader(rows, buildUnitHeaderOptions, t);

  const columns = useMemo(
    () =>
      buildAllocationsColumns(t, {
        unitHeaderSelect,
        onOpen,
        onViewDeclaration,
      }),
    [t, unitHeaderSelect, onOpen, onViewDeclaration],
  );

  const filterOptionsArgs = useMemo(
    () => ({ certificateType, extraFilters }),
    [certificateType, extraFilters],
  );
  const { data: filterOptions } = useGetAllocationFilterOptionsQuery(filterOptionsArgs);

  const filterDefinitions = useMemo(
    () =>
      buildAllocationFilterDefinitions(t, {
        materialOptions: filterOptions?.materialOptions ?? EMPTY_LIST,
        customerOptions: filterOptions?.customerOptions ?? EMPTY_LIST,
      }),
    [t, filterOptions],
  );
  const tableLabels = useMemo(() => buildTableLabels(t), [t]);

  const handleSearchChange = useCallback(
    (value) => {
      table.setSearch(value);
      table.setPage(1);
    },
    [table],
  );

  const handleFilterChange = useCallback(
    (values) => {
      table.setFilters(values);
      table.setPage(1);
    },
    [table],
  );

  return (
    <UnitTypeContext.Provider value={contextValue}>
      <Table
        className={className}
        ref={tableRef}
        columns={columns}
        data={rows}
        total={total}
        loading={isFetching}
        getRowId={getAllocationRowId}
        searchable
        selectable={Boolean(bulkActions)}
        bulkActions={bulkActions}
        onSearchChange={handleSearchChange}
        filters={filterDefinitions}
        onFilterChange={handleFilterChange}
        onPageChange={table.setPage}
        onPageSizeChange={table.setPageSize}
        rowActions={rowActions}
        rowMenuActions={rowMenuActions}
        actionsHeader={t('FIELD_ACTION')}
        actionsWidth={actionsWidth}
        emptyState={emptyState}
        noResults={noResults}
        labels={tableLabels}
      />
    </UnitTypeContext.Provider>
  );
};
